/**
 * DOM Utilities
 * Type-safe DOM query helpers
 *
 * @package StandOut
 * @since 2.0.0
 */
/**
 * Type-safe querySelector with null handling
 *
 * @param selector - CSS selector
 * @param parent - Parent element to search within (defaults to document)
 * @returns Element or null if not found
 *
 * @example
 * const nav = $<HTMLElement>('.main-navigation')
 * if (nav) nav.style.opacity = '1'
 */
export const $ = (selector, parent = document) => {
    return parent.querySelector(selector);
};
/**
 * Type-safe querySelectorAll
 *
 * @param selector - CSS selector
 * @param parent - Parent element to search within (defaults to document)
 * @returns Array of matching elements
 *
 * @example
 * const buttons = $$<HTMLButtonElement>('.button')
 * buttons.forEach(btn => btn.disabled = true)
 */
export const $$ = (selector, parent = document) => {
    return Array.from(parent.querySelectorAll(selector));
};
/**
 * Get element by ID with type safety
 *
 * @param id - Element ID (without #)
 * @returns Element or null if not found
 */
export const $id = (id) => {
    return document.getElementById(id);
};
/**
 * Create an element with optional attributes and children
 *
 * @param tag - HTML tag name
 * @param attrs - Optional attributes
 * @param children - Optional children (strings or elements)
 * @returns Created element
 *
 * @example
 * const span = createElement('span', { className: 'char' }, 'A')
 */
export const createElement = (tag, attrs, ...children) => {
    const element = document.createElement(tag);
    if (attrs) {
        Object.assign(element, attrs);
    }
    children.forEach((child) => {
        if (typeof child === "string") {
            element.appendChild(document.createTextNode(child));
        }
        else {
            element.appendChild(child);
        }
    });
    return element;
};
